// SidebarDefaultsView — admin define a ORDEM DEFAULT do menu lateral pra
// cada portal. Quando usuário não personalizou, recebe essa ordem.
//
// MVP: só "student" está realmente plugado. Os outros 3 portais aparecem como
// "em breve" — o admin pode salvar a ordem mesmo assim, e quando esses
// portais ganharem sidebar-order, já vão usar.

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Loader2,
  RefreshCw,
  Save,
  Settings
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { api, ApiError } from "./api";
import { openAlert } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

type Portal = "student" | "teacher" | "company" | "manager";

interface CatalogItem {
  key: string;
  label: string;
}

// Catálogo de chaves conhecidas por portal — DEVE bater com o sidebarOrder.ts
// de cada portal. Se um portal não tiver sidebarOrder plugado ainda, o admin
// pode salvar mesmo assim e fica pronto pra quando o portal usar.
const CATALOGS: Record<Portal, CatalogItem[]> = {
  student: [
    { key: "home", label: "Home" },
    { key: "aistudy", label: "Estudar com IA" },
    { key: "quickstudy", label: "Tira-dúvida" },
    { key: "conexoes", label: "Conexões" },
    { key: "peerchat", label: "Chat" },
    { key: "bancos", label: "Bancos" },
    { key: "descobrir", label: "Biblioteca de provas" },
    { key: "anotacoes", label: "Minhas anotações" },
    { key: "disciplinas", label: "Disciplinas" },
    { key: "provas", label: "Provas" },
    { key: "agenda", label: "Agenda" },
    // No portal do aluno, Notificações e Mensagens são fundidas em "Caixa
    // de entrada" (key canônica = notificacoes). Não listar 'mensagens'
    // separadamente pra não confundir o admin que está reordenando.
    { key: "notificacoes", label: "Caixa de entrada (notificações + mensagens)" },
    { key: "jogos", label: "Jogos" },
    { key: "gravador", label: "Gravador de Aulas" },
    { key: "compartilhamentos", label: "Compartilhamentos" },
    { key: "ia", label: "IA (configuração)" },
    { key: "pagamentos", label: "Pagamentos e consumo" },
    { key: "meusreports", label: "Reportar algo" },
    { key: "faq", label: "Perguntas frequentes" },
    { key: "minhaconta", label: "Minha conta" }
  ],
  teacher: [
    { key: "home", label: "Home" },
    { key: "agenda", label: "Agenda" },
    { key: "turmas", label: "Turmas" },
    { key: "disciplinas", label: "Disciplinas" },
    { key: "alunos", label: "Alunos" },
    { key: "bancos", label: "Bancos" },
    { key: "editor", label: "Editor de questões" },
    { key: "aulas", label: "Aulas" },
    { key: "mensagens", label: "Mensagens" }
  ],
  company: [
    { key: "home", label: "Home" },
    { key: "alunos", label: "Alunos" },
    { key: "professores", label: "Professores" },
    { key: "disciplinas", label: "Disciplinas" },
    { key: "turmas", label: "Turmas" },
    { key: "pagamentos", label: "Pagamentos" },
    { key: "branding", label: "Identidade visual" },
    { key: "configuracoes", label: "Configurações" }
  ],
  manager: [
    // Pessoas & Conteúdo
    { key: "organizacoes", label: "Organizações" },
    { key: "usuarios", label: "Usuários" },
    { key: "signup", label: "Convites (signup)" },
    { key: "professores", label: "Professores" },
    { key: "grupos", label: "Turmas" },
    { key: "bancos", label: "Bancos" },
    { key: "library", label: "Biblioteca de provas" },
    { key: "essaythemes", label: "Biblioteca de Redação" },
    { key: "recado", label: "Recado" },
    { key: "jogos", label: "Jogos" },
    // Pagamentos
    { key: "billing", label: "Billing" },
    { key: "paymentsandbox", label: "Sandbox MP (testes)" },
    { key: "credits", label: "Créditos (pré-pago)" },
    { key: "creditsplans", label: "Créditos & Planos" },
    { key: "payments", label: "Métodos de pagamento" },
    { key: "planos", label: "Planos (plataforma)" },
    { key: "assinaturas", label: "Assinaturas" },
    // IA
    { key: "aiconnections", label: "Conexões IA" },
    { key: "aistudy", label: "Estudar com IA" },
    { key: "aifeatureconfig", label: "Modelos IA por feature" },
    { key: "aiusersstatus", label: "IA por usuário" },
    { key: "aiusage", label: "Consumo de tokens" },
    { key: "aicreditsgate", label: "Créditos por feature" },
    { key: "quickstudyconfig", label: "Tira-dúvida (limites)" },
    { key: "quickstudyincidents", label: "Tira-dúvida (incidentes)" },
    // Comunicação
    { key: "notifications", label: "Notificações" },
    { key: "emailsettings", label: "Configurações de e-mail" },
    { key: "emailtemplates", label: "Templates de e-mail" },
    { key: "faq", label: "FAQ" },
    // Configurações
    { key: "player", label: "Player de Música" },
    { key: "sidebardefaults", label: "Ordem dos menus" },
    { key: "orphandefault", label: "Organização default" },
    { key: "studentvisibility", label: "Visibilidade no aluno" },
    { key: "branding", label: "Identidade visual" },
    { key: "featureflags", label: "Feature Flags" },
    // Operação
    { key: "userreports", label: "Bugs & Sugestões" },
    { key: "adminalerts", label: "Alertas de incidentes" }
  ]
};

const phase = signal<"loading" | "ready" | "error">("loading");
const saved = signal<Partial<Record<Portal, string[]>>>({});
const errMsg = signal<string | null>(null);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.getSidebarDefaults();
    saved.value = r.defaults;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

export function SidebarDefaultsView() {
  const [activePortal, setActivePortal] = useState<Portal>("student");

  useEffect(() => {
    void load();
  }, []);

  if (phase.value === "loading")
    return (
      <div class="space-y-3">
        <Skeleton class="h-12 w-full" />
        <Skeleton class="h-64 w-full" />
      </div>
    );
  if (phase.value === "error")
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {errMsg.value}
      </div>
    );

  return (
    <div class="space-y-4">
      <div class="rounded-md border border-violet-500/30 bg-violet-500/5 px-4 py-3 text-sm">
        <Settings className="mr-1 inline size-4 text-violet-600" />
        <strong>Ordem default por portal.</strong> Define como o menu lateral
        aparece pros usuários que ainda não personalizaram. Quem já arrastou
        e reordenou no painel dele mantém a customização — você não estraga
        nada salvando aqui.
      </div>

      {/* Tabs */}
      <div class="flex flex-wrap gap-1 border-b border-border">
        {(Object.keys(CATALOGS) as Portal[]).map((p) => (
          <PortalTab
            key={p}
            label={portalLabel(p)}
            active={activePortal === p}
            soon={p !== "student"}
            onClick={() => setActivePortal(p)}
          />
        ))}
      </div>

      <PortalEditor key={activePortal} portal={activePortal} />
    </div>
  );
}

function portalLabel(p: Portal): string {
  if (p === "student") return "Aluno";
  if (p === "teacher") return "Professor";
  if (p === "company") return "Organização";
  return "Admin";
}

function PortalTab({
  label,
  active,
  soon,
  onClick
}: {
  label: string;
  active: boolean;
  soon: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition-colors ${
        active
          ? "border-primary text-primary"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {label}
      {soon && (
        <span class="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
          Em breve
        </span>
      )}
    </button>
  );
}

function PortalEditor({ portal }: { portal: Portal }) {
  const catalog = CATALOGS[portal];
  const persisted = saved.value[portal];
  // Inicializa: usa o salvo se houver, senão usa o catálogo na ordem default.
  const initial = useMemo(() => {
    if (persisted && persisted.length > 0) {
      const known = new Set(catalog.map((c) => c.key));
      const seen = new Set<string>();
      const ordered: CatalogItem[] = [];
      for (const k of persisted) {
        if (!known.has(k) || seen.has(k)) continue;
        seen.add(k);
        const item = catalog.find((c) => c.key === k);
        if (item) ordered.push(item);
      }
      // Itens novos do catálogo que não estavam no salvo vão pro final.
      for (const c of catalog) {
        if (!seen.has(c.key)) ordered.push(c);
      }
      return ordered;
    }
    return catalog;
  }, [portal, persisted]);

  const [items, setItems] = useState<CatalogItem[]>(initial);
  const [saving, setSaving] = useState(false);

  // Reseta quando muda de portal/persisted.
  useEffect(() => {
    setItems(initial);
  }, [initial]);

  function move(idx: number, delta: -1 | 1) {
    const next = idx + delta;
    if (next < 0 || next >= items.length) return;
    const out = items.slice();
    const [moved] = out.splice(idx, 1);
    out.splice(next, 0, moved);
    setItems(out);
  }

  async function save() {
    setSaving(true);
    try {
      const next: Partial<Record<Portal, string[]>> = {
        ...saved.value,
        [portal]: items.map((i) => i.key)
      };
      const r = await api.updateSidebarDefaults(next);
      saved.value = r.defaults;
      void openAlert({
        title: "Ordem salva",
        message: `Default do portal "${portalLabel(portal)}" atualizado. Quem não personalizou já recebe na próxima visita.`
      });
    } catch (e) {
      void openAlert({
        title: "Erro ao salvar",
        message: friendlyError(e)
      });
    } finally {
      setSaving(false);
    }
  }

  async function resetToCatalog() {
    setItems(catalog);
  }

  const dirty =
    items.map((i) => i.key).join("|") !==
    (persisted && persisted.length > 0
      ? initial.map((i) => i.key).join("|")
      : catalog.map((c) => c.key).join("|"));

  return (
    <div class="space-y-3">
      {portal !== "student" && (
        <div class="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
          <AlertCircle className="mr-1 inline size-3.5" />
          Este portal ainda não consome a ordem default — você pode salvar
          mesmo assim e fica pronto pra quando ele plugar.
        </div>
      )}
      <ul class="divide-y divide-border overflow-hidden rounded-md border border-border bg-card/40">
        {items.map((it, idx) => (
          <li
            key={it.key}
            class="flex items-center gap-3 px-3 py-2 text-sm"
          >
            <span class="w-6 text-center font-mono text-[10px] text-muted-foreground tabular-nums">
              {idx + 1}
            </span>
            <div class="min-w-0 flex-1">
              <div class="font-medium">{it.label}</div>
              <code class="block text-[10px] text-muted-foreground">{it.key}</code>
            </div>
            <div class="flex gap-1">
              <button
                type="button"
                onClick={() => move(idx, -1)}
                disabled={idx === 0}
                title="Subir"
                aria-label="Subir"
                class="rounded-md border border-border bg-background p-1 hover:bg-accent disabled:opacity-30"
              >
                <ArrowUp className="size-3.5" />
              </button>
              <button
                type="button"
                onClick={() => move(idx, 1)}
                disabled={idx === items.length - 1}
                title="Descer"
                aria-label="Descer"
                class="rounded-md border border-border bg-background p-1 hover:bg-accent disabled:opacity-30"
              >
                <ArrowDown className="size-3.5" />
              </button>
            </div>
          </li>
        ))}
      </ul>
      <div class="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={resetToCatalog}
          disabled={saving}
          class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs hover:bg-accent disabled:opacity-50"
        >
          <RefreshCw className="size-3" />
          Restaurar ordem do código
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:brightness-110 disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Save className="size-3.5" />
          )}
          Salvar default
        </button>
      </div>
    </div>
  );
}
